
import React from 'react';
import { SessionInput } from '@/types/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Clipboard, FileText, Code, Zap, Info } from 'lucide-react';
import { toast } from 'sonner';

interface RawLlmResponseDialogProps {
  input: SessionInput;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RawLlmResponseDialog: React.FC<RawLlmResponseDialogProps> = ({ input, open, onOpenChange }) => {
  const handleCopyRawResponse = () => {
    if (input.raw_llm_response) {
      navigator.clipboard.writeText(input.raw_llm_response)
        .then(() => toast.success('Raw response copied to clipboard!'))
        .catch(() => toast.error('Failed to copy raw response'));
    }
  };

  const handleCopyToolCalls = () => {
    if (input.tool_calls) {
      navigator.clipboard.writeText(input.tool_calls)
        .then(() => toast.success('Tool calls copied to clipboard!'))
        .catch(() => toast.error('Failed to copy tool calls'));
    }
  };

  const handleCopyStudioBody = () => {
    if (input.raw_studio_request_body) {
      navigator.clipboard.writeText(input.raw_studio_request_body)
        .then(() => toast.success('Studio request body copied to clipboard!'))
        .catch(() => toast.error('Failed to copy studio request body'));
    }
  };

  const formatTokenCount = (count: number | null) => {
    return count !== null ? count.toLocaleString() : 'N/A';
  };

  const totalTokens = (input.input_token_count || 0) + (input.output_token_count || 0);

  const parseToolCalls = () => {
    if (!input.tool_calls) return [];
    try {
      return JSON.parse(input.tool_calls);
    } catch {
      return [];
    }
  };

  const toolCalls = parseToolCalls();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Raw LLM Response</DialogTitle>
              <DialogDescription>
                Complete raw response and metadata from the LLM for this turn
              </DialogDescription>
            </div>
            <div className="text-right text-xs text-muted-foreground">
              <div className="font-mono">Input ID: {input.id.substring(0, 8)}...</div>
              {input.created_at && (
                <div>{new Date(input.created_at).toLocaleString()}</div>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-grow overflow-hidden flex flex-col px-6 pb-6">
          <Tabs defaultValue="raw-response" className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-4 mb-4 flex-shrink-0">
              <TabsTrigger value="raw-response" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Raw Response
              </TabsTrigger>
              <TabsTrigger value="tool-calls" className="flex items-center gap-2">
                <Code className="h-4 w-4" />
                Tool Calls
              </TabsTrigger>
              <TabsTrigger value="tokens" className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Tokens
              </TabsTrigger>
              <TabsTrigger value="metadata" className="flex items-center gap-2">
                <Info className="h-4 w-4" />
                Metadata
              </TabsTrigger>
            </TabsList>

            {/* Raw Response Tab */}
            <TabsContent value="raw-response" className="flex-1 overflow-hidden flex flex-col data-[state=active]:flex">
              <div className="flex justify-between items-center mb-2 flex-shrink-0">
                <span className="text-sm text-muted-foreground">
                  Full raw LLM response
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyRawResponse}
                  disabled={!input.raw_llm_response}
                  className="flex items-center gap-2"
                >
                  <Clipboard className="h-4 w-4" />
                  Copy Response
                </Button>
              </div>
              <ScrollArea className="flex-1 border rounded-md">
                <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-words">
                  {input.raw_llm_response || <span className="text-muted-foreground italic">No raw response available</span>}
                </pre>
              </ScrollArea>
            </TabsContent>

            {/* Tool Calls Tab */}
            <TabsContent value="tool-calls" className="flex-1 overflow-hidden flex flex-col data-[state=active]:flex">
              <div className="flex justify-between items-center mb-2 flex-shrink-0">
                <span className="text-sm text-muted-foreground">
                  Parsed tool calls from response ({toolCalls.length} found)
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyToolCalls}
                  disabled={!input.tool_calls}
                  className="flex items-center gap-2"
                >
                  <Clipboard className="h-4 w-4" />
                  Copy JSON
                </Button>
              </div>
              <ScrollArea className="flex-1 border rounded-md">
                {toolCalls.length > 0 ? (
                  <div className="space-y-3 p-4">
                    {toolCalls.map((call: any, index: number) => (
                      <div key={index} className="border rounded-md p-3 bg-muted/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="default" className="font-mono text-xs">
                            {call.function?.name || 'unknown'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Call #{index + 1}
                          </span>
                        </div>
                        <pre className="text-xs font-mono whitespace-pre-wrap break-words p-2 bg-background rounded">
                          {JSON.stringify(call, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32 text-sm text-muted-foreground italic">
                    No tool calls found in this response
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Tokens Tab */}
            <TabsContent value="tokens" className="flex-1 overflow-auto data-[state=active]:flex">
              <div className="p-4 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold mb-3">Token Usage</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="border rounded-md p-3 bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Input Tokens</div>
                      <div className="text-2xl font-mono font-bold">
                        {formatTokenCount(input.input_token_count)}
                      </div>
                    </div>
                    <div className="border rounded-md p-3 bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Output Tokens</div>
                      <div className="text-2xl font-mono font-bold">
                        {formatTokenCount(input.output_token_count)}
                      </div>
                    </div>
                    <div className="border rounded-md p-3 bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Cached Tokens</div>
                      <div className="text-2xl font-mono font-bold text-amber-600 dark:text-amber-400">
                        {formatTokenCount(input.cached_token_count)}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-sm font-semibold mb-3">Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Tokens:</span>
                      <span className="font-mono font-bold">{totalTokens.toLocaleString()}</span>
                    </div>
                    {input.cached_token_count && input.cached_token_count > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Cache Hit Rate:</span>
                        <span className="font-mono font-bold">
                          {((input.cached_token_count / totalTokens) * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Execution Strategy:</span>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {input.execution_strategy || 'N/A'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Metadata Tab */}
            <TabsContent value="metadata" className="flex-1 overflow-auto data-[state=active]:flex">
              <ScrollArea className="h-full">
                <div className="p-4 space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Session Input Info</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ID:</span>
                        <span className="font-mono">{input.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Role:</span>
                        <Badge variant={input.role === 'model' ? 'default' : 'secondary'} className="font-mono text-xs">
                          {input.role || 'N/A'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Context Carrier:</span>
                        <Badge variant={input.is_context_carrier ? 'default' : 'secondary'}>
                          {input.is_context_carrier ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Created At:</span>
                        <span className="font-mono">
                          {input.created_at ? new Date(input.created_at).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Updated At:</span>
                        <span className="font-mono">
                          {input.updated_at ? new Date(input.updated_at).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-sm font-semibold mb-3">External Integration</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">External Conversation ID:</span>
                        <span className="font-mono max-w-[200px] truncate">
                          {input.external_conversation_id || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">External Turn ID:</span>
                        <span className="font-mono max-w-[200px] truncate">
                          {input.external_turn_id || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Idempotency Key:</span>
                        <span className="font-mono max-w-[200px] truncate">
                          {input.idempotency_key || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {input.thoughts && (
                    <>
                      <Separator />
                      <div>
                        <h3 className="text-sm font-semibold mb-3">Thoughts</h3>
                        <ScrollArea className="max-h-[150px] border rounded-md">
                          <pre className="p-3 text-xs whitespace-pre-wrap">
                            {input.thoughts}
                          </pre>
                        </ScrollArea>
                      </div>
                    </>
                  )}

                  {input.raw_studio_request_body && (
                    <>
                      <Separator />
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-sm font-semibold">Raw Studio Request Body</h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCopyStudioBody}
                            className="flex items-center gap-2"
                          >
                            <Clipboard className="h-3 w-3" />
                            Copy
                          </Button>
                        </div>
                        <ScrollArea className="max-h-[200px] border rounded-md">
                          <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-words">
                            {input.raw_studio_request_body}
                          </pre>
                        </ScrollArea>
                      </div>
                    </>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RawLlmResponseDialog;